import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import {
  BENCHMARK_LAST5_WEIGHT,
  BENCHMARK_MAX_TRANSACTIONS,
  BENCHMARK_LATEST_TXN_WEIGHT,
  BENCHMARK_RECENCY_WINDOW_MONTHS,
  BENCHMARK_SOURCES,
  benchmarkSourceDefinition,
} from '../config/ci-options.config';
import { BenchmarkingForm, TransactionForm } from '../models/ci-form.model';
import { BenchmarkBasis } from '../models/ci-result.model';
import { ciBenchmarkRate, InventoryProperty } from '../models/ci-property.model';
import { CiPropertySearchComponent } from '../components/ci-property-search.component';
import { UiCalculatedBoxComponent } from '../ui/ui-calculated-box.component';
import { UiInputComponent } from '../ui/ui-input.component';
import { formatPsf } from '../../../core/utils/format.util';
import { PropertyService } from '../../../_services/properties/property.service';
import { CiFormService } from '../services/ci-form.service';
import { CiService } from '../../../_services/ci.service';

interface AnchorProperty {
  id: string;
  name: string;
  psf: number;
}

/** "02 · Benchmarking" — choose the source, then let the base rate follow. */
@Component({
  selector: 'ci-step-transactions',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, CiPropertySearchComponent, UiInputComponent, UiCalculatedBoxComponent],
  templateUrl: './step-transactions.component.html',
})
export class StepTransactionsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly formService = inject(CiFormService);
  readonly maxTransactions = BENCHMARK_MAX_TRANSACTIONS;

  addTransaction(): void {
    this.formService.addTransaction(this.form());
  }

  removeTransaction(index: number): void {
    const row = this.form().controls.transactions.at(index);
    this.formService.removeTransaction(this.form(), index);
    if (!this.form().controls.transactions.controls.includes(row)) {
      this.comparables.update((states) => {
        const next = new Map(states);
        next.delete(row);
        return next;
      });
    }
  }

  private readonly properties = inject(PropertyService);
  private readonly ciApi = inject(CiService);
  readonly form = input.required<BenchmarkingForm>();
  readonly basis = input.required<BenchmarkBasis>();
  /** Same control step 4 validates — editable here too so choosing "Use
   *  Similar Listed Properties" / "Use Anchor Properties" is immediately
   *  actionable instead of a dead end with nothing to fill in until a later
   *  step. */
  readonly manualRateControl = input.required<FormControl<number | null>>();
  readonly microMarket = input<string>('');
  readonly addSimilar = output<void>();
  readonly removeSimilar = output<number>();

  sourceLabel(): string {
    return benchmarkSourceDefinition(this.form().controls.source.value).label;
  }

  readonly sources = BENCHMARK_SOURCES;
  readonly last5Weight = `${BENCHMARK_LAST5_WEIGHT * 100}%`;
  readonly latestWeight = `${BENCHMARK_LATEST_TXN_WEIGHT * 100}%`;
  readonly recencyWindow = BENCHMARK_RECENCY_WINDOW_MONTHS;

  psf = formatPsf;
  readonly comparables = signal(new Map<TransactionForm, {
    property: InventoryProperty;
    loading: boolean;
    error: string | null;
  }>());

  constructor() {
    effect((onCleanup) => {
      const form = this.form();
      const update = () => this.updateComparableAverage();
      const rowsSubscription = form.controls.transactions.valueChanges.subscribe(update);
      const sourceSubscription = form.controls.source.valueChanges.subscribe(update);
      update();
      onCleanup(() => {
        rowsSubscription.unsubscribe();
        sourceSubscription.unsubscribe();
      });
    });
  }

  private updateComparableAverage(): void {
    if (this.isTransactionsSource()) return;
    const values = this.form().controls.transactions.controls
      .map((row) => this.toPositiveNumber(row.controls.psf.value))
      .filter((value): value is number => value !== null);
    const average = values.length ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length) : null;
    if (this.manualRateControl().value !== average) this.manualRateControl().setValue(average);
  }
  private readonly anchorRows = new WeakMap<TransactionForm, {
    search: FormControl<string>;
    name: FormControl<string>;
    psf: FormControl<number | null>;
    showForm: boolean;
    selectedId: string | null;
    showResults: boolean;
  }>();

  anchorState(row: TransactionForm) {
    let state = this.anchorRows.get(row);
    if (!state) {
      state = {
        search: new FormControl('', { nonNullable: true }),
        name: new FormControl('', { nonNullable: true }),
        psf: new FormControl<number | null>(null),
        showForm: false,
        selectedId: null,
        showResults: false,
      };
      this.anchorRows.set(row, state);
    }
    return state;
  }

  readonly anchorProperties = signal<AnchorProperty[]>([
    { id: 'ozone-urbana-aspire', name: 'Ozone Urbana Aspire', psf: 9800 },
    { id: 'rmz-latitude', name: 'RMZ Latitude', psf: 22400 },
    { id: 'nitesh-chelsea', name: 'Nitesh Chelsea', psf: 16800 },
    { id: 'vaishnavi-north-24', name: 'Vaishnavi North 24', psf: 14300 },
  ]);

  selectComparable(row: TransactionForm, property: InventoryProperty): void {
    const selection = { property, loading: true, error: null as string | null };
    this.comparables.update((states) => new Map(states).set(row, selection));
    // Each property contributes its single CI benchmark, never its transaction history.
    row.patchValue({
      psf: this.toPositiveNumber(property.ciBenchmarkPsf),
      source: property.propertyName,
      date: this.today(),
    });
    row.markAsDirty();

    const isCurrent = () => this.form().controls.transactions.controls.includes(row)
      && this.comparables().get(row) === selection;

    this.properties.getProperty(property.id).pipe(
      switchMap((response: any) => {
        const details = response?.data;
        const ci = details?.ciIntelligence ?? details?.cipIntelligence
          ?? details?.ciPublicReport ?? details?.ciRecord ?? details?.ciCalculation ?? details?.ci;
        if (this.ciRate(ci)) return of(ci);
        const id = details?.ciRecordId ?? details?.ciId ?? details?.ciCalculationId;
        return id ? this.ciApi.getCiRecordById(id) : of(null);
      }),
      catchError(() => of(null)),
      switchMap((ci) => {
        if (!isCurrent() || this.ciRate(ci) || property.ciBenchmarkPsf) return of(ci);
        // Older records may only be linked through the CI dashboard.
        return this.ciApi.getDashboardData().pipe(
          switchMap((dashboard) => {
            const match = dashboard.recentIntel?.find((record) => record.propertyId === property.id)
              ?? dashboard.recentIntel?.find(
                (record) => this.normaliseName(record.property) === this.normaliseName(property.propertyName)
                  && (!record.microMarket || !property.microMarket || this.normaliseName(record.microMarket) === this.normaliseName(property.microMarket)),
              );
            if (!match) return of(null);
            const summary = { benchmarkPsf: ciBenchmarkRate(match), updatedAt: match.lastUpdated };
            return match.id ? this.ciApi.getCiRecordById(match.id).pipe(
              switchMap((record) => of(this.ciRate(record) ? record : summary)),
              catchError(() => of(summary)),
            ) : of(summary);
          }),
        );
      }),
      catchError(() => of(null)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((ci) => {
      if (!isCurrent()) return;
      const rate = this.ciRate(ci) ?? this.toPositiveNumber(property.ciBenchmarkPsf);
      row.patchValue({
        psf: rate,
        date: this.toInputDate(ci?.updatedAt ?? ci?.createdAt) || row.controls.date.value,
      });
      this.comparables.update((states) => new Map(states).set(row, {
        property,
        loading: false,
        error: rate ? null : 'No CI benchmark is available for this property. Select another property.',
      }));
    });
  }

  clearComparable(row: TransactionForm): void {
    this.comparables.update((states) => {
      const next = new Map(states);
      next.delete(row);
      return next;
    });
    row.reset({ psf: null, source: '', date: '' });
    row.markAsDirty();
  }

  private ciRate(ci: any): number | null {
    return ciBenchmarkRate(ci);
  }

  private today(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }

  private toPositiveNumber(value: unknown): number | null {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
  }

  private toInputDate(value: string | null | undefined): string {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const slashDate = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
    if (slashDate) {
      const [, day, month, year] = slashDate;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toISOString().slice(0, 10);
  }

  private normaliseName(value: string | null | undefined): string {
    return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  filteredAnchorProperties(row: TransactionForm): AnchorProperty[] {
    const query = this.anchorState(row).search.value.trim().toLowerCase();
    return this.anchorProperties().filter((property) => !query || property.name.toLowerCase().includes(query));
  }

  closeAnchorSearch(row: TransactionForm, event: FocusEvent): void {
    if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null)) {
      this.anchorState(row).showResults = false;
    }
  }

  selectAnchor(row: TransactionForm, property: AnchorProperty): void {
    this.anchorState(row).selectedId = property.id;
    this.anchorState(row).showResults = false;
    row.patchValue({ psf: property.psf, source: property.name, date: this.today() });
    row.markAsDirty();
  }

  clearAnchor(row: TransactionForm): void {
    this.anchorState(row).selectedId = null;
    row.reset({ psf: null, source: '', date: '' });
    row.markAsDirty();
  }

  saveAnchor(row: TransactionForm): void {
    const state = this.anchorState(row);
    const name = state.name.value.trim();
    const psf = this.toPositiveNumber(state.psf.value);
    if (!name || !psf || !Number.isFinite(psf) || psf <= 0) return;
    const anchor = { id: `anchor-${Date.now()}`, name, psf };
    this.anchorProperties.update((properties) => [...properties, anchor]);
    this.selectAnchor(row, anchor);
    state.name.reset('');
    state.psf.reset();
    state.showForm = false;
  }

  isTransactionsSource(): boolean {
    return this.form().controls.source.value === 'transactions';
  }

  last5Average(): string {
    const b = this.basis();
    return b.last5AveragePsf !== null ? formatPsf(b.last5AveragePsf) : '—';
  }

  usableCount(): number {
    return this.form()
      .controls.transactions.controls.filter((row) => {
        const v = row.getRawValue();
        return !!v.psf && !!v.date;
      }).length;
  }

  latestTransaction(): string {
    const b = this.basis();
    return b.latestTransactionPsf !== null ? formatPsf(b.latestTransactionPsf) : '—';
  }

  latestTransactionDate(): string {
    const b = this.basis();
    if (!b.latestTransactionDate) return '—';
    return b.latestTransactionAgeMonths != null
      ? `${b.latestTransactionDate} · ${b.latestTransactionAgeMonths} months ago`
      : b.latestTransactionDate;
  }

  baseBenchmark(): string {
    const b = this.basis();
    return b.baseBenchmarkPsf > 0 ? formatPsf(b.baseBenchmarkPsf) : '—';
  }

  calculationLogic(): string {
    if (this.form().controls.source.value === 'similar-listings') return 'Average of the similar property CI benchmarks.';
    if (!this.isTransactionsSource()) return 'Average of the anchor property benchmarks.';
    return `<=${this.recencyWindow}M: ${this.last5Weight} Last-5 Avg + ${this.latestWeight} Last Txn`;
  }
}
